import { FC, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import type { RootState } from "store";
import type { ZoomRange } from "features/chart";
import type { ServerBasePrice } from "services/types";
import type { ChartType as TChartType } from "shared/model";
import { selectIndicatorsInstances } from "features/indicators/model";
import { IndicatorPanel } from "features/indicators/ui/indicator-panel/IndicatorPanel";
import { ChartOverlay } from "features/indicators/ui/chart-overlay/ChartOverlay";
import { Chart } from "./Chart";
import styles from "./ChartWithIndicators.module.css";

const OSCILLATOR_NAMES = new Set(["acceleratorOscillator", "aDX", "aroon", "mACD", "osMA", "rSI", "stochasticOscillator"]);
const ON_CHART_NAMES = new Set(["alligator", "bollingerBands", "donchianChannel", "fractal", "fractalChaosBands", "ichimokuKinkoHyo", "parabolicSAR", "superTrend", "zigZag"]);

interface Props {
  symbol: string;
  chartType: TChartType;
  onReady: () => void;
}

export const ChartWithIndicators: FC<Props> = ({ symbol, chartType, onReady }) => {
  const sharedDataRef = useRef<ServerBasePrice[]>([]);
  const sharedZoomRef = useRef<ZoomRange>({ start: 0, end: 0 });
  const panelDrawsRef = useRef<Record<string, () => void>>({});

  const instances = useSelector((state: RootState) => selectIndicatorsInstances(state));
  const panels = instances.filter(i => OSCILLATOR_NAMES.has(i.name) && i.visible !== false);
  const onChartInstances = instances.filter(i => ON_CHART_NAMES.has(i.name) && i.visible !== false);

  const handleChartFrame = useCallback((data: ServerBasePrice[], zoom: ZoomRange) => {
    sharedDataRef.current = data;
    sharedZoomRef.current = zoom;
    Object.values(panelDrawsRef.current).forEach(fn => fn());
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.chartMain}>
        <Chart
          symbol={symbol}
          chartType={chartType}
          onReady={onReady}
          onFrame={handleChartFrame}
        />
        {onChartInstances.map((inst, idx) => (
          <ChartOverlay
            key={inst.id}
            instance={inst}
            index={idx}
            dataRef={sharedDataRef}
            zoomRangeRef={sharedZoomRef}
            onRegisterDraw={(fn) => { panelDrawsRef.current[inst.id] = fn; }}
            onUnregisterDraw={() => { delete panelDrawsRef.current[inst.id]; }}
          />
        ))}
      </div>
      {panels.map(inst => (
        <IndicatorPanel
          key={inst.id}
          instance={inst}
          dataRef={sharedDataRef}
          zoomRangeRef={sharedZoomRef}
          onRegisterDraw={(fn) => { panelDrawsRef.current[inst.id] = fn; }}
          onUnregisterDraw={() => { delete panelDrawsRef.current[inst.id]; }}
        />
      ))}
    </div>
  );
};
